import React, { useCallback, useRef, useState } from "react";
import client from "@culturehq/client";
import { BooleanField, Form, Loader, Panel, Success, SubmitButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch } from "app/hooks";
import { areEventsEnabled, t, Trans } from "app/utils";

const EMAILS = {
  commentReplied: t("a colleague replies to your comment"),
  photoCommented: t("a colleague comments on a photo you uploaded"),
  pointBonus: t("an admin awards you with a bonus to your Culture Score"),
  mentioned: t("a colleague mentions you somewhere in the community"),
  photoTagged: t("a colleague tags you in a photo"),
  connectionCreated: t("a colleague starts following you"),
  connectionDoubled: t("a colleague that you follow follows you back "),
  postCommented: t("a colleague comments on your post"),
  answeredStory: t("a colleague answers a story that you created"),
  storyCommented: t("a colleague comments on your story"),
  commentOfInterest: t("a colleague comments on a post/story that you have commented")
};

const EVENT_EMAILS = {
  eventInvite: t("you have been invited to an event"),
  eventNotification: t("a host sends an email update to the guests on an event"),
  postEventSurvey: t("a post-event survey is available for an event you were invited to"),
  rsvpConfirmation: t("you RSVP to an event"),
  eventCommented: t("a colleague comments on an event you are hosting"),
  eventHostingCapped: t("an event you are hosting has reached its maximum capacity"),
  eventInterestsAligned: t("an event is created with one or more of your interests")
};

const RECOGNITION_EMAILS = {
  recognized: t("you have been recognized by a user"),
  recognitionGivenCommented: t("a colleague comments on a recognition you gave"),
  recognitionReceivedCommented: t("a colleague comments on a recognition you received")
};

const ADMIN_EMAILS = {
  feedbackSubmitted: t("a colleague submits feedback to the admins"),
  storyReviewer: t("a colleague answers a story and you need to review it")
};

const getEmails = profile => {
  const { organizationAdmin, organization: { recognitionsEnabled } } = profile;
  let emails = EMAILS;
  if (organizationAdmin) {
    emails = { ...emails, ...ADMIN_EMAILS };
  }

  if (areEventsEnabled(profile)) {
    emails = { ...emails, ...EVENT_EMAILS };
  }

  if (recognitionsEnabled) {
    emails = { ...emails, ...RECOGNITION_EMAILS };
  }

  return emails;
};

const ProfileEmails = () => {
  const { profile } = useProfile();
  const fetchState = useFetch(client.getProfileEmailSettings, [profile]);

  const emailsRef = useRef(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = useCallback(
    options => {
      setSuccess(false);

      client.updateProfileEmailSettings(options)
        .then(({ emailSettings }) => {
          if (emailsRef.current) {
            fetchState.onUpdate(() => ({ emailSettings }));
            setSuccess(true);
          }
        });
    },
    [fetchState, emailsRef, setSuccess]
  );

  const emails = getEmails(profile);

  return (
    <Panel>
      <Panel.Heading>
        <Trans>Email Notifications</Trans>
      </Panel.Heading>
      <Panel.Body>
        <p>
          <Trans>
            Here you can toggle on and off which email notifications you
            receive. Currently, you will receive an email when:
          </Trans>
        </p>
        <div ref={emailsRef}>
          <Loader loading={fetchState.fetching}>
            {success && (
              <Success>
                <Trans>Success!</Trans>
              </Success>
            )}

            {fetchState.json.emailSettings && (
              <Form initialValues={fetchState.json.emailSettings} onSubmit={onSubmit}>
                {Object.keys(emails).map(key => (
                  <BooleanField key={key} name={key}>
                    {emails[key]}
                  </BooleanField>
                ))}

                <SubmitButton primary>
                  {submitting => (
                    submitting ? <Trans>Submitting...</Trans> : <Trans>Save Changes</Trans>
                  )}
                </SubmitButton>
              </Form>
            )}
          </Loader>
        </div>
      </Panel.Body>
    </Panel>
  );
};

export default ProfileEmails;
